import React from 'react';
import { 
  CreditCard, 
  IndianRupee, 
  Shield, 
  CheckCircle, 
  AlertCircle,
  Smartphone,
  Landmark,
  Wallet,
  ArrowLeft,
  Lock,
  Mail,
  Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PaymentTerms = () => {
  const navigate = useNavigate();

  const paymentMethods = [
    { icon: CreditCard, name: "Credit & Debit Cards", description: "Visa, Mastercard, RuPay, Maestro", color: "text-blue-600" },
    { icon: Landmark, name: "Net Banking", description: "All major Indian banks", color: "text-green-600" },
    { icon: Smartphone, name: "UPI", description: "GPay, PhonePe, Paytm, BHIM", color: "text-purple-600" },
    { icon: Wallet, name: "Mobile Wallets", description: "Various mobile wallet options", color: "text-orange-600" }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <button 
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <CreditCard className="text-[#1e3a5f]" size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">Payment Terms and Conditions</h1>
          <p className="text-gray-600 text-md max-w-2xl mx-auto">
            At DP Jewellers, we are committed to providing a seamless and secure shopping experience. 
            Please review our payment terms below to understand our billing processes, accepted payment methods, and security measures.
          </p>
          <div className="w-20 h-1 bg-[#1e3a5f] mx-auto mt-4 rounded-full"></div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <CreditCard className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">1. Accepted Payment Methods</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            We offer a variety of secure payment options for your convenience, including:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {paymentMethods.map((method, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <method.icon className={method.color} size={20} />
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{method.name}</p>
                  <p className="text-gray-500 text-xs">{method.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <IndianRupee className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">2. Pricing and Taxes</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            All prices listed on our website are in <strong>Indian Rupees (INR)</strong> and are inclusive of 
            Goods and Services Tax (GST) unless otherwise stated. Gold rates are dynamic and subject to market fluctuations. 
            The final price at the time of checkout is binding.
          </p>
          <div className="mt-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
            <p className="text-amber-800 text-sm flex items-center gap-2">
              <span className="text-lg">💰</span>
              Gold rates are updated daily based on market prices
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Lock className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">3. Secure Transactions</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-3">
            We prioritize the security of your financial data. All online transactions are processed through secure, 
            encrypted payment gateways. DP Jewellers does not store your complete credit/debit card information or net banking credentials.
          </p>
          <div className="flex items-center gap-2 text-green-600 text-sm">
            <Shield size={16} />
            <span>256-bit SSL Encryption</span>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <CheckCircle className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">4. Payment Confirmation</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            Once your payment is successfully processed, you will receive an order confirmation email and SMS with the 
            transaction details. If an amount is deducted from your account but you do not receive a confirmation, 
            please contact our customer support immediately.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <AlertCircle className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">5. Failed Transactions</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            In the event of a payment failure where the amount has been deducted from your account, it is usually 
            refunded automatically by the bank within <strong>7 business days</strong>. Please check with your bank if the refund is delayed.
          </p>
          <div className="mt-3 bg-red-50 p-3 rounded-lg border border-red-200">
            <p className="text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              Amount deducted but order not confirmed? Contact us immediately!
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <IndianRupee className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">6. Advance Payments</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            For customized orders or special bookings, an advance payment may be required. This advance is 
            <strong> non-refundable</strong> in case of cancellation once the manufacturing process has begun.
          </p>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
          <h3 className="font-semibold text-[#1e3a5f] mb-3 text-lg">Need Payment Assistance?</h3>
          <p className="text-gray-700 text-sm mb-4">
            Our customer support team is here to help you with any payment-related queries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="tel:+918953786652"
              className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#153057] text-white px-4 py-2 rounded-lg transition-colors text-sm"
            >
              <Phone size={16} />
              Call Us: +91 89537 86652
            </a>
            <a 
              href="mailto:info@dpjewellersballia.in"
              className="inline-flex items-center gap-2 border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-4 py-2 rounded-lg transition-colors text-sm"
            >
              <Mail size={16} />
              Email Support
            </a>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-6">
          <div className="bg-white rounded-lg shadow p-3 flex items-center gap-2">
            <Lock size={16} className="text-green-600" />
            <span className="text-xs text-gray-600">Secure Payment</span>
          </div>
          <div className="bg-white rounded-lg shadow p-3 flex items-center gap-2">
            <Shield size={16} className="text-green-600" />
            <span className="text-xs text-gray-600">100% Safe</span>
          </div>
          <div className="bg-white rounded-lg shadow p-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-green-600" />
            <span className="text-xs text-gray-600">Instant Confirmation</span>
          </div>
        </div>
        <div className="text-center text-gray-400 text-xs mt-8">
          <p>DP Jewellers - Trusted Since 1941</p>
          <p className="mt-1">© {new Date().getFullYear()} DP Jewellers. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentTerms;